import ActivityContextProvider from "PFApp/activities/show/activity_context_provider";
import PageTabs from "PFComponents/page/page_tabs";
import PropTypes from "prop-types";
import React, { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { underscored } from "underscore.string";

import css from "./audit_planner_page.module.scss";
import ActivityTabs from "./parts/activity_tabs";
import AllocationButton from "./parts/allocation_button";
import FilterBar from "./parts/filter_bar";
import useRoleFilters from "./parts/use_role_filters";

const AuditPlannerPage = ({
  data,
  tab,
  tabs,
  changeTab,
  counters,
  fetchAllEngagements,
  workflowAutomationContext
}) => {
  const { onFilterChange, onOrderChange, onFiltersReset, noFiltersSelected } = data;
  const { workflowAutomation, isAuditAdmin } = workflowAutomationContext;

  const { roleFilters, roleSelectedFilters, onRoleFilterChange, onRoleFiltersReset, noRoleFiltersSelected } =
    useRoleFilters(tab.id);

  const { Component, showPlanner } = tab;
  const history = useHistory();
  const { roleId, engagementId } = useParams();

  const onClick = (value) => {
    history.push(`/audit_planner/${underscored(value.id)}`);
    changeTab(value.id);
  };

  const closeActivityView = useCallback(() => {
    history.push(`/audit_planner/${tab.id}/${engagementId}`);
  }, [engagementId, tab]);

  const handleFilterChange = (filter, value, valueObject) => {
    if (filter.templateKey === "role") {
      onRoleFilterChange(filter, value, valueObject);
      filter.name === "approver_id" && onFilterChange(filter, value, valueObject);
    } else {
      onFilterChange(filter, value, valueObject);
    }
  };

  return (
    <div className={css.root}>
      <div className={css.headerNavigation}>
        <PageTabs
          items={tabs}
          active={tab.id}
          handleClick={onClick}
          rootClassName={css.pageTabs}
          counters={counters}
        />
        <div className={css.allocationButtonWrapper}>
          {tab.id === "control_centre" && isAuditAdmin && (
            <AllocationButton workflowAutomationContext={workflowAutomationContext} />
          )}
        </div>
      </div>
      {roleId ? (
        <ActivityContextProvider key={roleId} id={roleId} onActivityUpdate={fetchAllEngagements}>
          <ActivityTabs close={closeActivityView} />
        </ActivityContextProvider>
      ) : (
        <FilterBar
          tab={tab.id}
          tabs={tabs}
          changeTab={changeTab}
          data={data.data}
          roleFilters={roleFilters}
          counters={counters}
          onFilterChange={handleFilterChange}
          onOrderChange={onOrderChange}
          onFiltersReset={() => {
            onRoleFiltersReset();
            onFiltersReset();
          }}
          noFiltersSelected={noFiltersSelected && noRoleFiltersSelected}
          showPlanner={tab.showPlanner}
          setShowPlanner={tab.setShowPlanner}
        />
      )}
      <div style={roleId && (showPlanner ? { height: "0px", overflow: "hidden" } : { display: "none" })}>
        <Component
          {...data}
          roleFilters={roleSelectedFilters}
          showPlanner={showPlanner}
          tab={tab}
          tabs={tabs}
          changeTab={changeTab}
          counters={counters}
          fetchAllEngagements={fetchAllEngagements}
          workflowAutomation={workflowAutomation}
        />
      </div>
    </div>
  );
};

AuditPlannerPage.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({
      entries: PropTypes.arrayOf(PropTypes.object).isRequired,
      meta: PropTypes.object.isRequired
    }).isRequired,
    onFilterChange: PropTypes.func.isRequired,
    onOrderChange: PropTypes.func.isRequired,
    onFiltersReset: PropTypes.func.isRequired,
    noFiltersSelected: PropTypes.bool,
    fetchData: PropTypes.func.isRequired
  }).isRequired,
  tab: PropTypes.shape({
    id: PropTypes.string.isRequired,
    Component: PropTypes.elementType.isRequired,
    showPlanner: PropTypes.bool,
    setShowPlanner: PropTypes.func
  }).isRequired,
  tabs: PropTypes.arrayOf(PropTypes.object).isRequired,
  changeTab: PropTypes.func.isRequired,
  counters: PageTabs.propTypes.counters,
  fetchAllEngagements: PropTypes.func.isRequired,
  workflowAutomationContext: PropTypes.shape({
    startWorkflowAutomation: PropTypes.func.isRequired,
    setAlwaysAllocate: PropTypes.func.isRequired,
    workflowAutomation: PropTypes.shape({
      auto_filling: PropTypes.number.isRequired,
      auto_shortlisting: PropTypes.number.isRequired,
      pending: PropTypes.number.isRequired,
      auto_run: PropTypes.bool
    }).isRequired,
    isAuditAdmin: PropTypes.bool,
    setWorkflowAutomation: PropTypes.func,
    fetchWorkflowAutomation: PropTypes.func
  })
};

export default AuditPlannerPage;
